import { type ReactNode, type MouseEvent } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Link as MuiLink } from '@mui/material';
import { truncateMiddle } from '@liminal/utils/truncate';
import { FONT_STACK_BODY } from '@liminal/brand/font-faces';

// Ledger entries can carry non-user account IDs (system accounts like
// "External Bank (On-ramp)"); linking those to /users/<id> fires the user
// detail page's API fan-out with a non-UUID and pages on-call.
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface UserIdLinkProps {
  userId: string | null | undefined;
  children?: ReactNode;
  full?: boolean;
  fontSize?: number | string;
  onClick?: () => void;
}

export function UserIdLink({
  userId,
  children,
  full = false,
  fontSize = 12,
  onClick,
}: UserIdLinkProps) {
  if (!userId) return <>{'—'}</>;
  if (!UUID_PATTERN.test(userId)) {
    return (
      <Box
        component="span"
        sx={children ? undefined : { fontFamily: FONT_STACK_BODY, fontSize }}
      >
        {children ?? userId}
      </Box>
    );
  }
  const handleClick = (e: MouseEvent) => {
    e.stopPropagation();
    onClick?.();
  };
  return (
    <MuiLink
      component={RouterLink}
      to={`/users/${userId}`}
      onClick={handleClick}
      underline="hover"
      sx={children ? undefined : { fontFamily: FONT_STACK_BODY, fontSize }}
    >
      {children ?? (full ? userId : truncateMiddle(userId))}
    </MuiLink>
  );
}
